// ACDC Custom File ECB-107 - Cart Notification

(() => {
  let hideTimeout = null;

  /**
   * Initializes the cart notification functionality
   */
  function init() {
    const notification = document.getElementById('cart-notification');
    if (!notification) return;

    const textElement = notification.querySelector('.cart-notification__text');
    const progressFill = notification.querySelector('.cart-notification__progress-fill');
    const closeButton = notification.querySelector('.cart-notification__close');
    const duration = parseInt(notification.dataset.duration, 10) || 3000;
    const textTemplate = notification.dataset.text || 'Product added to cart!';

    // START ACDC Custom Code | ECSR-48: Queue overlapping messages instead of
    // letting a second show() call silently stomp a message that is still on screen.
    // Without this, two independent triggers (e.g. add-to-cart and the compare-limit
    // warning) sharing this single notification instance could overwrite one another
    // mid-display, so one message would never be read/announced.
    let isShowing = false;
    const pendingQueue = [];
    // END ACDC Custom Code

    // START ACDC Custom Code | ECSR-542: Pause/resume state for the auto-dismiss
    // timer and progress bar while the message is hovered or focused within.
    // NOTE: `duration` above is read once here from data-duration at init time --
    // the element's dataset is not re-read at runtime, so pausing tracks the
    // remaining time in JS state below rather than trying to mutate
    // notification.dataset.duration mid-display.
    let remainingMs = duration;
    let timerStartedAt = 0;
    let isPaused = false;
    let isHovered = false;
    let isFocusedWithin = false;

    // ECSR-542: the live region is .cart-notification__content, not the outer
    // wrapper -- the wrapper also contains the manual close control and the
    // progress bar, and aria-atomic="true" would announce those on every
    // update. Fall back to the wrapper if the markup hasn't been updated.
    const liveRegion = notification.querySelector('.cart-notification__content') || notification;

    // ECSR-542: capture the live region's default ARIA presentation (the
    // polite status announcement used for routine confirmations like "added to
    // cart") once, so a more assertive message can be announced and then the
    // default restored for the next routine message.
    const defaultRole = liveRegion.getAttribute('role');
    const defaultAriaLive = liveRegion.getAttribute('aria-live');

    // ECSR-542 QA: `hide()` schedules a 300ms teardown that owns the end-of-life
    // transaction (set [hidden], clear the text, release the slot, drain the
    // ECSR-48 queue). It was neither tracked nor guarded, and the close
    // button's `isShowing` check does not stop a second call because isShowing
    // stays true for the whole fade. Two hide() calls inside that window
    // scheduled two teardowns: the first drained the queue and displayed the
    // next message, the second then blanked that freshly-shown message and
    // dropped it -- silently losing exactly the message ECSR-48's queue exists
    // to protect. Track the teardown and make hide() idempotent while in flight.
    let hideTransitionTimeout = null;
    // END ACDC Custom Code

    // START ACDC Custom Code | ECSR-571: resolve the fade-out length from CSS
    // rather than assuming it. cart-notification.css shortens the fade under
    // prefers-reduced-motion, but the teardown below was hard-coded to 300ms,
    // so for those shoppers the message had finished fading ~200ms before it
    // was actually cleared -- a visible empty gap where the message had been.
    // Reading the custom property keeps the two in step by construction, and
    // reading it per call means a shopper who changes the OS setting mid-session
    // is respected without a reload.
    const DEFAULT_FADE_MS = 300;

    function getFadeDurationMs() {
      const raw = getComputedStyle(notification)
        .getPropertyValue('--cart-notification-fade-duration')
        .trim();
      if (!raw) return DEFAULT_FADE_MS;

      // CSS <time> is expressed in either ms or s; parseFloat drops the unit,
      // so the suffix decides the scale.
      const value = parseFloat(raw);
      if (!Number.isFinite(value) || value < 0) return DEFAULT_FADE_MS;
      return raw.endsWith('ms') ? value : value * 1000;
    }
    // END ACDC Custom Code

    function clearHideTimeout() {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    }

    // ECSR-542: (re)start the auto-dismiss timer for the given number of
    // milliseconds, tracking when it started so a pause can compute how much
    // time is left.
    function startHideTimer(ms) {
      clearHideTimeout();
      timerStartedAt = Date.now();
      remainingMs = ms;
      hideTimeout = setTimeout(() => {
        hide();
      }, ms);
    }

    // ECSR-542: pause the auto-dismiss timer and freeze the progress bar
    // exactly where it is, so hovering/focusing the message stops it from
    // disappearing mid-read.
    function pauseTimer() {
      // ACDC Custom Code | ECSR-571: dropped the `!hideTimeout` bail-out. The
      // countdown is not armed for the first frames after show(), and refusing
      // to pause in that window meant a hover landing there was discarded and
      // the message counted down anyway. Record the pause regardless; if the
      // timer has not started yet, remainingMs is already the full duration and
      // show()'s start step sees isPaused and stands down.
      if (!isShowing || isPaused) return;
      isPaused = true;

      if (hideTimeout) {
        const elapsed = Date.now() - timerStartedAt;
        remainingMs = Math.max(remainingMs - elapsed, 0);
        clearHideTimeout();
      }

      if (progressFill) {
        const computedTransform = getComputedStyle(progressFill).transform;
        progressFill.style.transition = 'none';
        progressFill.style.transform = computedTransform === 'none' ? 'scaleX(1)' : computedTransform;
      }
    }

    // ECSR-542: resume the auto-dismiss timer for whatever time was left when
    // it was paused, and continue the progress bar from where it froze.
    function resumeTimer() {
      if (!isShowing || !isPaused) return;
      isPaused = false;

      if (remainingMs <= 0) {
        hide();
        return;
      }

      startHideTimer(remainingMs);

      if (progressFill) {
        requestAnimationFrame(() => {
          progressFill.style.transition = `transform ${remainingMs}ms linear`;
          progressFill.style.transform = 'scaleX(0)';
        });
      }
    }

    // ECSR-542: hover and focus can overlap (e.g. hovering the close button
    // while it also holds focus), so only resume once neither is active.
    function updatePauseState() {
      if (isHovered || isFocusedWithin) {
        pauseTimer();
      } else {
        resumeTimer();
      }
    }

    /**
     * Shows the notification with the given text
     * @param {string} text - The notification text to display
     * @param {Object} [options] - Display options
     * @param {boolean} [options.assertive] - Announce this message assertively
     *   instead of the default polite status announcement, for messages that
     *   report a refused action rather than confirm a routine success.
     * @param {boolean} [options.fluid] - Let the message box size itself to its
     *   content instead of using the fixed 150px box. For longer sentences that
     *   would otherwise wrap to several cramped lines on small phones. Routine
     *   confirmations omit this and keep the fixed box unchanged.
     */
    function show(text, options = {}) {
      // START ACDC Custom Code | ECSR-48: Defer instead of overwrite when a message is already visible
      if (isShowing) {
        pendingQueue.push({ text, options });
        return;
      }
      isShowing = true;
      // END ACDC Custom Code

      // ECSR-542: reset pause state for the new message
      isPaused = false;
      // ACDC Custom Code | ECSR-571: reset the remaining time up front too, so a
      // pause landing before the countdown is armed (see below) freezes this
      // message's full duration rather than whatever was left of the last one.
      remainingMs = duration;

      // Clear any existing timeout
      clearHideTimeout();

      // START ACDC Custom Code | ECSR-542: Announce a refused-action message
      // (e.g. the compare limit) more assertively than the default polite
      // status announcement used for routine confirmations. Restore the
      // default presentation otherwise so cart confirmation messaging is
      // unchanged.
      if (options.assertive) {
        liveRegion.setAttribute('role', 'alert');
        liveRegion.setAttribute('aria-live', 'assertive');
      } else {
        if (defaultRole !== null) liveRegion.setAttribute('role', defaultRole);
        if (defaultAriaLive !== null) liveRegion.setAttribute('aria-live', defaultAriaLive);
      }
      // END ACDC Custom Code

      // START ACDC Custom Code | ECSR-571: opt-in fluid width. The fixed 150px
      // box suits the short add-to-bag confirmation but squeezes the
      // compare-limit sentence into ~4 cramped lines on small phones. toggle()
      // also removes the class, so a routine confirmation reusing this single
      // shared instance after a fluid message is guaranteed the original box.
      notification.classList.toggle('cart-notification--fluid', !!options.fluid);
      // END ACDC Custom Code

      // Update the text
      textElement.textContent = text;

      // Reset progress bar
      if (progressFill) {
        progressFill.style.transition = 'none';
        progressFill.style.transform = 'scaleX(1)';
      }

      // Show the notification
      notification.removeAttribute('hidden');

      // START ACDC Custom Code | ECSR-48: Lift the header stacking context above
      // other page overlays (e.g. the compare drawer, z-index: 50) while a message
      // is visible. #cart-notification lives inside <store-header> (z-index: 30),
      // so its own z-index: 9999 is capped by that ancestor's stacking context and
      // gets painted underneath any overlay layered above the header -- silently
      // hiding this notification any time it fires while such an overlay is open.
      document.body.classList.add('has-notification-visible');
      // END ACDC Custom Code

      // START ACDC Custom Code | ECSR-571: arm the countdown only once the
      // message is genuinely interactive, and decide paused-vs-running from a
      // live hit test rather than from flags.
      //
      // The component is `pointer-events: none` until `.is-visible` is applied,
      // so for the first frame(s) after show() it cannot be hovered at all: a
      // pointer already resting where the message appears produces no
      // mouseenter and `isHovered` stays false. The countdown used to be armed
      // synchronously here -- inside that dead window -- so a hover arriving in
      // it was silently missed and the message kept counting down. That is the
      // "pause is not completely reliable" report.
      //
      // Asking the DOM (`:hover` / activeElement) in the same frame the timer
      // starts closes the window: by then `.is-visible` is committed,
      // pointer-events is auto, and :hover reflects reality whether or not any
      // mouse event ever fired.
      requestAnimationFrame(() => {
        // A manual close can land before this frame runs (e.g. the pointer is
        // already down on the close control when the message appears). hide()
        // has then already removed `is-visible` and scheduled the teardown, so
        // re-adding it here would snap the message back to fully opaque for the
        // rest of the fade and leave the class set for the next message, which
        // would then appear with no fade-in at all.
        if (!isShowing || hideTransitionTimeout) return;

        notification.classList.add('is-visible');

        requestAnimationFrame(() => {
          // Same guard again: the message may have been dismissed in the frame
          // between the two callbacks. Never arm a countdown for a message that
          // is already on its way out.
          if (!isShowing || hideTransitionTimeout) return;

          if (notification.matches(':hover')) isHovered = true;
          if (notification.contains(document.activeElement)) isFocusedWithin = true;

          if (isHovered || isFocusedWithin || isPaused) {
            isPaused = true;
            remainingMs = duration;
            return;
          }

          if (progressFill) {
            progressFill.style.transition = `transform ${duration}ms linear`;
            progressFill.style.transform = 'scaleX(0)';
          }

          startHideTimer(duration);
        });
      });
      // END ACDC Custom Code
    }

    /**
     * Hides the notification
     */
    function hide() {
      // ECSR-542 QA: idempotent while a teardown is already in flight, so a
      // second dismissal during the 300ms fade (manual close racing the
      // auto-dismiss timer, or two quick activations of the close button)
      // cannot schedule a second teardown that would blank and drop the
      // message the first teardown just pulled off the ECSR-48 queue.
      if (hideTransitionTimeout) return;

      clearHideTimeout();
      isPaused = false;
      notification.classList.remove('is-visible');

      // Wait for transition to complete before hiding
      hideTransitionTimeout = setTimeout(() => {
        hideTransitionTimeout = null;
        notification.setAttribute('hidden', '');
        textElement.textContent = '';

        // Reset progress bar
        if (progressFill) {
          progressFill.style.transition = 'none';
          progressFill.style.transform = 'scaleX(1)';
        }

        // ECSR-542 QA: the notification is now [hidden] (visibility: hidden), so
        // anything focused inside it has just become unfocusable and the browser
        // would drop focus to <body>, throwing a keyboard shopper back to the top
        // of the page. Hand focus to the cart link this message is anchored to,
        // but only if focus was still inside the notification.
        const focusWasInside = notification.contains(document.activeElement);

        // ECSR-542 QA: re-derive hover/focus from live state rather than trusting
        // flags that were last set by events on a now-hidden element -- a
        // mouseleave/focusout dispatched (or not) as the element is hidden must
        // not decide whether the NEXT queued message starts paused.
        isHovered = notification.matches(':hover');
        isFocusedWithin = notification.contains(document.activeElement);

        // START ACDC Custom Code | ECSR-48: Release the slot, then show any queued message
        isShowing = false;
        if (pendingQueue.length) {
          const next = pendingQueue.shift();
          show(next.text, next.options);
        } else {
          document.body.classList.remove('has-notification-visible');
        }
        // END ACDC Custom Code

        if (focusWasInside) {
          const cartLink = document.querySelector('.header__cart-wrapper a, #cart-icon');
          if (cartLink) cartLink.focus();
        }
        // ACDC Custom Code | ECSR-571: was a hard-coded 300ms. Now matches the
        // fade the shopper is actually being shown, including the shortened
        // reduced-motion one.
      }, getFadeDurationMs());
    }

    // START ACDC Custom Code | ECSR-542: manual close control -- let a shopper
    // dismiss the message immediately instead of waiting for the timer.
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        if (!isShowing) return;
        hide();
      });
    }
    // END ACDC Custom Code

    // START ACDC Custom Code | ECSR-542: pause the auto-dismiss timer while the
    // message is hovered or has keyboard focus within it.
    notification.addEventListener('mouseenter', () => {
      isHovered = true;
      updatePauseState();
    });
    notification.addEventListener('mouseleave', () => {
      isHovered = false;
      updatePauseState();
    });
    notification.addEventListener('focusin', () => {
      isFocusedWithin = true;
      updatePauseState();
    });
    notification.addEventListener('focusout', (evt) => {
      // Only treat focus as having left the notification once it actually
      // moves outside of it, not when it moves between two focusable elements
      // inside it (e.g. the close button).
      if (notification.contains(evt.relatedTarget)) return;
      isFocusedWithin = false;
      updatePauseState();
    });
    // END ACDC Custom Code

    /**
     * Handles the cart add event
     * @param {CustomEvent} evt - The cart add event
     */
    function handleCartAdd(evt) {
      if (!evt.detail || !evt.detail.cart) return;

      try {
        // START ACDC Custom Code | ECB-115: Direct notification text for meal plans
        if (evt.detail.notificationText) {
          show(evt.detail.notificationText);
          return;
        }
        // END ACDC Custom Code

        // Get the product title from the cart data
        const variantId = evt.detail.variantId;
        const cart = evt.detail.cart;
        // Use loose equality to handle string/number variantId mismatch
        const item = cart.items.find((item) => String(item.variant_id) === String(variantId));

        const productTitle = evt.detail.productTitle || (item ? (item.product_title || 'Product') : 'Custom Meal');
        const quantityAdded = evt.detail.quantityAdded || 1;

        // Replace placeholders with actual values
        let notificationText = textTemplate
          .replace(/\{product_title\}/gi, productTitle)
          .replace(/\{product_quantity\}/gi, quantityAdded);

        // Show the notification
        show(notificationText);
      } catch (error) {
        console.error('Cart notification error:', error);
      }
    }

    // Listen for cart:add events
    document.addEventListener('on:cart:add', handleCartAdd);

    // START ACDC Custom Code | ECSR-48: Generic notification event for other features (e.g. compare limit)
    // ECSR-542: forward an `assertive` flag so a refused-action message (e.g.
    // compare limit) can be announced more insistently than a routine one.
    document.addEventListener('on:notification:show', (evt) => {
      // ECSR-571: also forward a `fluid` flag so a caller with a longer message
      // can let the box size itself instead of using the fixed 150px one.
      if (evt.detail && evt.detail.text) {
        show(evt.detail.text, {
          assertive: !!evt.detail.assertive,
          fluid: !!evt.detail.fluid
        });
      }
    });
    // END ACDC Custom Code
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
